import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSend, FiCalendar, FiLoader, FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { leadsAPI } from '../services/api';
import { whatsappAPI } from '../services/whatsappAPI';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 640px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const ResumoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[6]};
`;

const ResumoItem = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  text-align: center;
`;

const ResumoValor = styled.div`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  margin-bottom: ${theme.spacing[1]};
`;

const ResumoLabel = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
  }

  input {
    width: 20px;
    height: 20px;
    accent-color: ${theme.colors.primary[500]};
    cursor: pointer;
  }
`;

const RadioText = styled.span`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-weight: 500;
`;

const DateTimeWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[4]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[6]};
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[8]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.text.muted};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
  margin: 0;
`;

const LeadsConfirmarEnvio = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const rawState = location.state;
  const leadIds = rawState?.leadIdsParaMensagens ?? [];
  const totalDoState = rawState?.totalLeadsSelecionados ?? leadIds.length ?? 0;

  const qtyInicial = totalDoState || (Array.isArray(leadIds) ? leadIds.length : 0) || 0;
  const [totalLeads, setTotalLeads] = useState(qtyInicial);
  const [totalContatos, setTotalContatos] = useState(0);
  const [contatos, setContatos] = useState([]); // lista { id, lead_id, nome, telefone } para uso no envio
  const [loading, setLoading] = useState(!!(leadIds && leadIds.length));
  const [error, setError] = useState(null);
  const [tipoEnvio, setTipoEnvio] = useState('imediato');
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [horaAgendamento, setHoraAgendamento] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    const ids = Array.isArray(leadIds) ? leadIds : [];
    if (ids.length === 0) {
      navigate('/leads/preparar-mensagens', { replace: true });
      return;
    }

    const qty = totalDoState > 0 ? totalDoState : ids.length;
    setTotalLeads(qty);

    const carregarContagem = async () => {
      try {
        setLoading(true);
        setError(null);
        const idsParaApi = ids.map((id) => String(id).trim()).filter(Boolean);
        const response = await leadsAPI.contagemEnvio(idsParaApi);
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          const totalL = data.totalLeads ?? qty;
          const listaContatos = Array.isArray(data.contatos) ? data.contatos : [];
          const totalC = data.totalContatos ?? listaContatos.length;
          setTotalLeads(totalL);
          setTotalContatos(totalC);
          setContatos(listaContatos);
        }
      } catch (err) {
        console.error('[LEADS_CONFIRMAR] Erro ao carregar contagem:', err);
        setError('Erro ao carregar totais. Tente novamente.');
        setTotalLeads(qty);
        setTotalContatos(0);
        setContatos([]);
      } finally {
        setLoading(false);
      }
    };

    carregarContagem();
  }, [leadIds, totalDoState, navigate]);



  const getMinDateTime = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleConfirmar = async () => {
    if (tipoEnvio === 'agendar') {
      if (!dataAgendamento || !horaAgendamento) {
        setError('Informe a data e a hora do agendamento.');
        return;
      }
    }

    setEnviando(true);
    setError(null);

    try {
      // TODO: integrar com API de envio/agendamento de e-mail quando existir
      const payload = {
        leadIds,
        contatos, // lista { id, lead_id, nome, telefone, etapa_codigo, mensagem_etapa, etapa_id }
        totalLeads,
        totalContatos,
        enviarImediatamente: tipoEnvio === 'imediato',
        ...(tipoEnvio === 'agendar' && {
          dataAgendamento: `${dataAgendamento}T${horaAgendamento}:00`,
          agendado: true
        })
      };

      if (tipoEnvio === 'imediato') {
        // contatos vêm da API com a PRÓXIMA etapa e PRÓXIMA mensagem (ex.: lead na etapa 1 -> mensagem da etapa 2)
        const gruposEtapa = {};
        
        contatos.forEach(contato => {
          if (!contato.telefone) return;
          // Só envia para quem tem próxima etapa e próxima mensagem (lead na última etapa fica de fora)
          const mensagem = contato.mensagem_etapa || '';
          if (!mensagem.trim()) return;

          const codigoEtapa = contato.etapa_codigo || 'SEM_ETAPA';
          const etapaId = contato.etapa_id; 
          
          // Agrupar por ID da etapa (próxima etapa) para envio e, depois, atualizar lead para essa etapa
          // Se não tiver etapaId (ex.: lead já na última etapa), agrupa por código apenas para envio
          const chaveGrupo = etapaId ? `ID_${etapaId}` : `CODE_${codigoEtapa}`;
          
          if (!gruposEtapa[chaveGrupo]) {
            gruposEtapa[chaveGrupo] = {
              etapa: codigoEtapa,
              etapaId: etapaId,
              mensagem: mensagem,
              constatos: [],
              leadIds: [] 
            };
          }
          
          gruposEtapa[chaveGrupo].constatos.push(contato.telefone);
          if (contato.lead_id && !gruposEtapa[chaveGrupo].leadIds.includes(contato.lead_id)) {
            gruposEtapa[chaveGrupo].leadIds.push(contato.lead_id);
          }
        });

        // Debug: Verificar grupos formados
        console.debug('[LeadsConfirmarEnvio] Grupos formados:', gruposEtapa);

        const etapasPayload = Object.values(gruposEtapa).map(({ etapa, mensagem, constatos }) => ({
          etapa,
          mensagem,
          constatos
        }));

        if (etapasPayload.length === 0) {
           setError('Nenhum telefone válido encontrado para envio.');
           setEnviando(false);
           return;
        }

        const payloadWebhook = {
          id_cliente: 1,
          etapas: etapasPayload
        };

        if (import.meta.env.DEV) {
          console.debug('[LeadsConfirmarEnvio] Payload Webhook:', payloadWebhook);
        }

        await whatsappAPI.enviarLeadParaWebhook(payloadWebhook);
        
        // Se sucesso (200), atualizar etapas dos leads
        // Se sucesso (200), atualizar etapas dos leads
        try {
          const updates = Object.values(gruposEtapa)
            .filter(g => g.etapaId && g.leadIds.length > 0)
            .map(g => ({
              leadIds: g.leadIds,
              etapaId: g.etapaId
            }));

          console.debug('[LeadsConfirmarEnvio] Updates preparados:', updates);

          if (updates.length > 0) {
            const updateRes = await leadsAPI.atualizarEtapaLote(updates);
            console.debug('[LeadsConfirmarEnvio] Resultado atualização:', updateRes.data);
          } else {
            console.warn('[LeadsConfirmarEnvio] Nenhum lead para atualizar (sem próxima etapa definida?)');
          }
        } catch (updateErr) {
          console.error('[LeadsConfirmarEnvio] Erro ao atualizar etapas após envio:', updateErr);
          // Opcional: Mostrar toast/erro visual se crítico
        }

        // Sucesso
        setTimeout(() => {
          setEnviando(false);
          navigate('/leads/preparar-mensagens');
        }, 800);

      } else {
        // Placeholder: agendamento registrado
        setTimeout(() => {
          setEnviando(false);
          navigate('/leads/preparar-mensagens');
        }, 800);
      }
    } catch (err) {
      console.error('Erro ao confirmar envio:', err);
      setError('Erro ao processar. Tente novamente.');
      setEnviando(false);
    }
  };

  if (leadIds.length === 0) {
    return null;
  }

  const totalLeadsExibir = totalLeads > 0 ? totalLeads : qtyInicial;

  const handleVoltar = () => navigate('/leads/preparar-mensagens');

  if (loading && totalLeadsExibir === 0 && totalContatos === 0) {
    return (
      <Layout title="Confirmar envio de mensagens">
        <Container>
          <Header>
            <BackButton type="button" onClick={handleVoltar}>
              <FiArrowLeft />
              Voltar
            </BackButton>
            <Title>Confirmar envio de mensagens</Title>
          </Header>
          <LoadingContainer>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            <p style={{ marginTop: theme.spacing[4] }}>Carregando totais...</p>
          </LoadingContainer>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Confirmar envio de mensagens">
      <Container>
        <Header>
          <BackButton type="button" onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>Confirmar envio de mensagens</Title>
        </Header>

        <Card>
          <ResumoGrid>
            <ResumoItem>
              <ResumoValor>{totalLeadsExibir}</ResumoValor>
              <ResumoLabel>Total de leads</ResumoLabel>
            </ResumoItem>
            <ResumoItem>
              <ResumoValor>{loading ? '...' : totalContatos}</ResumoValor>
              <ResumoLabel>Total de contatos</ResumoLabel>
            </ResumoItem>
          </ResumoGrid>

          <SectionTitle>Como deseja enviar?</SectionTitle>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="tipoEnvio"
                value="imediato"
                checked={tipoEnvio === 'imediato'}
                onChange={() => setTipoEnvio('imediato')}
              />
              <FiSend size={20} color={theme.colors.primary[500]} />
              <RadioText>Enviar imediatamente</RadioText>
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="tipoEnvio"
                value="agendar"
                checked={tipoEnvio === 'agendar'}
                onChange={() => setTipoEnvio('agendar')}
              />
              <FiCalendar size={20} color={theme.colors.primary[500]} />
              <RadioText>Agendar envio</RadioText>
            </RadioLabel>
          </RadioGroup>

          {tipoEnvio === 'agendar' && (
            <DateTimeWrapper>
              <InputGroup>
                <Label htmlFor="dataAgendamento">Data do envio</Label>
                <Input
                  id="dataAgendamento"
                  type="date"
                  value={dataAgendamento}
                  onChange={(e) => setDataAgendamento(e.target.value)}
                  min={getMinDateTime()}
                />
              </InputGroup>
              <InputGroup>
                <Label htmlFor="horaAgendamento">Horário</Label>
                <Input
                  id="horaAgendamento"
                  type="time"
                  value={horaAgendamento}
                  onChange={(e) => setHoraAgendamento(e.target.value)}
                />
              </InputGroup>
            </DateTimeWrapper>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonGroup>
            <PrimaryButton
              type="button"
              onClick={handleConfirmar}
              disabled={enviando || (tipoEnvio === 'agendar' && (!dataAgendamento || !horaAgendamento))}
            >
              {enviando ? (
                <>
                  <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                  Processando...
                </>
              ) : tipoEnvio === 'imediato' ? (
                <>
                  <FiSend />
                  Enviar agora
                </>
              ) : (
                <>
                  <FiCalendar />
                  Agendar envio
                </>
              )}
            </PrimaryButton>
          </ButtonGroup>
        </Card>
      </Container>
    </Layout>
  );
};

export default LeadsConfirmarEnvio;
